/**
 * Document Entity - Application document attachments
 * 
 * Tracks uploaded files like transcripts, health forms, etc.
 * Per user decision D3: Document requirements are configurable.
 */
import { z } from 'zod';
import { ResourceId } from '@triedge/domain-core/types';
import { DocumentId, ApplicationId, UserId, ChildApplicationId } from '../types/branded';
import { ApplicationStatus, DocumentCategory } from '../types/enums';

// ============================================================================
// Application Document Entity
// ============================================================================

export const DocumentStorageProviderSchema = z.enum(['s3']);
export type DocumentStorageProvider = z.infer<typeof DocumentStorageProviderSchema>;

export const ApplicationDocumentSchema = z.object({
  id: DocumentId,
  applicationId: ApplicationId,

  // Resource reference (no embedded file payload)
  resourceId: ResourceId,
  
  // Classification
  category: DocumentCategory,

  // Attachment metadata
  fileName: z.string().min(1).max(255).optional(),
  contentType: z.string().min(1).max(255).optional(),
  sizeBytes: z.number().int().nonnegative().optional(),
  childApplicationIds: z.array(ChildApplicationId).max(20).optional(),

  // Storage metadata (resourceId remains canonical pointer)
  storageProvider: DocumentStorageProviderSchema.optional(),
  storageKey: z.string().min(1).max(1024).optional(),
  storageBucket: z.string().min(1).max(255).optional(),
  storageRegion: z.string().min(1).max(255).optional(),
  
  // Audit
  uploadedBy: UserId,
  uploadedAt: z.number().int(), // Unix seconds
  
  // Timestamps
  createdAt: z.number().int(),
  updatedAt: z.number().int(),
});
export type ApplicationDocument = z.infer<typeof ApplicationDocumentSchema>;

// ============================================================================
// Input Types
// ============================================================================

export const UploadDocumentInputSchema = z.object({
  applicationId: ApplicationId,
  resourceId: ResourceId,
  category: DocumentCategory,
  fileName: z.string().min(1).max(255).optional(),
  contentType: z.string().min(1).max(255).optional(),
  sizeBytes: z.number().int().nonnegative().optional(),
  childApplicationIds: z.array(ChildApplicationId).max(20).optional(),
  storageProvider: DocumentStorageProviderSchema.optional(),
  storageKey: z.string().min(1).max(1024).optional(),
  storageBucket: z.string().min(1).max(255).optional(),
  storageRegion: z.string().min(1).max(255).optional(),
});
export type UploadDocumentInput = z.infer<typeof UploadDocumentInputSchema>;

export const DeleteDocumentInputSchema = z.object({
  documentId: DocumentId,
});
export type DeleteDocumentInput = z.infer<typeof DeleteDocumentInputSchema>;

// ============================================================================
// Document Requirement (Configuration Type)
// Per user decision D3: Configurable document requirements
// ============================================================================

export const DocumentRequirementSchema = z.object({
  category: DocumentCategory,
  requiredAtStatus: ApplicationStatus.array(),
  description: z.string(),
  isConditional: z.boolean().default(false),
  // Optional: function name to check if requirement applies
  conditionKey: z.string().optional(),
});
export type DocumentRequirement = z.infer<typeof DocumentRequirementSchema>;
